import { useState } from 'react'
import { fetchExport } from './researchExportData'

// XLS / PDF download buttons for the Research weekday charts.
//
// Both formats pull the SAME bulk payload — the full drill-down for every
// weekday of every week (or month) at once. Fetching it slice by slice would be
// ~35 round trips and would blow the SWA gateway's ~45s budget, so the export
// endpoint pre-groups the lot in a single call.

type Kind = 'xlsx' | 'pdf'

export function ResearchExportButtons({ scope, month, monthLabel }: {
  scope: 'month' | 'season'
  /** Calendar month 1-12 — required when scope is 'month'. */
  month?: number
  monthLabel?: string
}) {
  const [busy, setBusy] = useState<Kind | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = async (kind: Kind) => {
    if (busy) return
    setBusy(kind)
    setError(null)
    try {
      // exceljs and jsPDF are ~1MB apiece — kept out of the initial bundle and
      // pulled in only when someone actually asks for a download.
      const [payload, writer] = await Promise.all([
        fetchExport(scope, month),
        kind === 'xlsx' ? import('./researchXlsx') : import('./researchExportPdf'),
      ])
      const label = monthLabel ?? 'Month'
      if (kind === 'xlsx') {
        const w = writer as typeof import('./researchXlsx')
        if (scope === 'month') await w.buildMonthWorkbook(payload, label)
        else await w.buildSeasonWorkbook(payload)
      } else {
        const w = writer as typeof import('./researchExportPdf')
        if (scope === 'month') w.buildMonthPdf(payload, label)
        else w.buildSeasonPdf(payload)
      }
    } catch (e) {
      setError(String((e as Error)?.message ?? e))
    } finally {
      setBusy(null)
    }
  }

  const btn = (kind: Kind, label: string): React.CSSProperties => ({
    background: '#fff',
    color: '#0b3d2e',
    border: '1px solid #0b3d2e',
    borderRadius: 6,
    padding: '3px 10px',
    cursor: busy ? 'wait' : 'pointer',
    fontWeight: 700,
    fontSize: '0.72rem',
    opacity: busy && busy !== kind ? 0.5 : 1,
    whiteSpace: 'nowrap',
    ...(label ? {} : {}),
  })

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <button
        type="button"
        onClick={() => run('xlsx')}
        disabled={busy != null}
        style={btn('xlsx', 'x')}
        title={scope === 'month'
          ? 'Excel workbook — a sheet per week, a whole-month sheet, and top-ten services'
          : 'Excel workbook — a sheet per month, a season-to-date sheet, and top-ten services and ticket types'}
      >{busy === 'xlsx' ? 'Building…' : 'XLS'}</button>
      <button
        type="button"
        onClick={() => run('pdf')}
        disabled={busy != null}
        style={btn('pdf', 'p')}
        title={scope === 'month'
          ? 'PDF — a page per week plus the whole month and top-ten services'
          : 'PDF — season to date, a page per month, and top-ten services and ticket types'}
      >{busy === 'pdf' ? 'Building…' : 'PDF'}</button>
      {error && <span style={{ fontSize: '0.7rem', color: '#a33' }}>{error}</span>}
    </div>
  )
}
